/**
 * "NOT YET" IS NOT "NO", and a screen that cannot tell them apart accuses the
 * studio of a broken sign-in every time a Jr Staff logs in.
 *
 * PR #1045 round two, both review lenses. A promoted Jr Staff dancer holds two
 * sessions, and they do not start together on purpose: lib/dancer-session-context
 * flips `state` to 'signed-in' the moment the cookie is confirmed and only THEN
 * redeems the minted custom token, because the dancer's own portal must never
 * wait on Firebase to open. For the second or two in between there is no
 * principal, so the ID token claim is unreadable and reads false, which is the
 * right answer for a route gate and the wrong sentence for a person: the sub
 * board tile told her, in words, that her sign-in needed the studio's
 * attention. Every login.
 *
 * So the boolean stays exactly as it was, failing closed at every gate, and
 * this answers the third state beside it. It is pure and imports nothing so a
 * test can walk the whole login sequence in node.
 *
 * NOTHING HERE MAY OPEN A DOOR. 'pending' means unknown, and an unknown must
 * never be routed as staff; it exists so a surface can stay quiet instead of
 * making a claim it cannot support yet.
 */

export type JrStaffPromotionState = 'pending' | 'promoted' | 'none';

export interface JrStaffPromotionInput {
  /** The dancer cookie is confirmed. Nothing is pending before that. */
  signedIn: boolean;
  /** Firebase Auth has not settled, so no claim can have been read. */
  firebaseLoading: boolean;
  /** A minted token is being redeemed right now. */
  promotionPending: boolean;
  /** The ID token claim: true, false, or null for not answered yet. */
  claim: boolean | null;
}

export function jrStaffPromotionState({
  signedIn,
  firebaseLoading,
  promotionPending,
  claim,
}: JrStaffPromotionInput): JrStaffPromotionState {
  // No dancer session, nothing to promote. Also the resting answer for an
  // adult account and a signed-out visitor.
  if (!signedIn) return 'none';
  // Proven staff wins outright: once the claim has answered true, a later
  // status re-check that momentarily re-arms the exchange must not flicker a
  // working tile back to a spinner.
  if (claim === true) return 'promoted';
  if (firebaseLoading || promotionPending || claim === null) return 'pending';
  return 'none';
}
